import type { Workshop } from "@/content/workshops";
import type { UserProfile } from "@/content/users";
import {
  ROLE_ANALYST,
  ROLE_DIRECTOR,
  ROLE_ENGINEER,
  ROLE_FACILITATOR,
  ROLE_OBSERVER,
} from "@/features/auth/roles";

/**
 * Per-workshop crew rosters. WS4 entries are ported from the
 * placeholder team in `_legacy/ws4-session.js` lines 42-91
 * (`WS4_SESSION.team`) with the same colour palette legacy uses
 * (blue / melon / green / orange) and a paired AI agent per member.
 *
 * Workshops other than WS4 carry an empty roster — the CrewPanel and
 * MeetCrewModal render an empty state in that case.
 */
export type CrewAccentColor = "blue" | "cyan" | "green" | "orange" | "melon" | "red";

export type CrewAgent = {
  name: string;
  type: string;
  description: string;
  color: CrewAccentColor;
};

export type CrewMember = {
  id: string;
  name: string;
  role: string;
  bio?: string;
  avatar?: string;
  /** Role-tier badge — "LEAD", "STRATEGY", "TECH", "OPS", … */
  badge?: string;
  /** Accent colour for the human card half. */
  color?: CrewAccentColor;
  /** Paired AI agent (smart_toy row in legacy). */
  agent?: CrewAgent;
};

/**
 * Per-role presentation for roster-derived crew — badge, accent, bio, and
 * a paired XAES agent — so a roster member renders with the SAME depth as
 * the authored WS4 crew (CrewCard's HUMAN + AGENT rows), not a bare
 * name/role chip. Keyed by the display-string roles on `UserProfile.role`.
 */
type RosterRoleDetail = {
  badge: string;
  color: CrewAccentColor;
  bio: string;
  agent: CrewAgent;
};

const ROSTER_ROLE_DETAIL: Record<string, RosterRoleDetail> = {
  [ROLE_FACILITATOR]: {
    badge: "LEAD",
    color: "blue",
    bio: "Runs the session arc — opening, activity flow, and outcome capture.",
    agent: {
      name: "XAES-OPS",
      type: "Session Conductor",
      description:
        "Keeps the agenda timed, captures decisions to the asset locker, and surfaces drift.",
      color: "blue",
    },
  },
  [ROLE_DIRECTOR]: {
    badge: "DIRECTOR",
    color: "melon",
    bio: "Owns the program arc — outcomes, priorities, and the cross-workshop throughline.",
    agent: {
      name: "XAES-HELM",
      type: "Program Strategist",
      description:
        "Tracks the rollup across workshops and flags where outcomes diverge from the charter.",
      color: "melon",
    },
  },
  [ROLE_ANALYST]: {
    badge: "ANALYST",
    color: "cyan",
    bio: "Turns workshop signal into findings, assumptions, and traceable decisions.",
    agent: {
      name: "XAES-SCOPE",
      type: "Insight Synthesiser",
      description:
        "Mines transcripts + captures for candidate findings and links them back to source.",
      color: "cyan",
    },
  },
  [ROLE_ENGINEER]: {
    badge: "TECH",
    color: "green",
    bio: "Grounds the design in what the platform can actually ship and operate.",
    agent: {
      name: "XAES-FORGE",
      type: "Systems Co-pilot",
      description:
        "Pressure-tests handoffs and integration assumptions against the target architecture.",
      color: "green",
    },
  },
  [ROLE_OBSERVER]: {
    badge: "OBSERVER",
    color: "orange",
    bio: "Holds context and keeps the record honest — the workshop's institutional memory.",
    agent: {
      name: "XAES-LENS",
      type: "Context Recorder",
      description:
        "Watches the room, timestamps key moments, and keeps the playback faithful.",
      color: "orange",
    },
  },
};

const DEFAULT_ROSTER_DETAIL: RosterRoleDetail = {
  badge: "CREW",
  color: "cyan",
  bio: "Workshop participant on the XAES program crew.",
  agent: {
    name: "XAES-AIDE",
    type: "Crew Co-pilot",
    description: "Surfaces relevant context and captures contributions to the record.",
    color: "cyan",
  },
};

/**
 * Map the auth roster (seed mode from `content/users.ts`, or the live
 * Zitadel join) to fully-formed crew members — badge, accent, bio, and a
 * paired agent derived from each user's role — so they match the WS4 crew's
 * level of detail. CREW-ROSTER uses this as the per-workshop fallback so
 * every workshop shows the real participants instead of an empty state;
 * WS4's hand-authored crew still wins where present. Live federated upgrade
 * is DEP-CREW.
 */
export function crewFromRoster(users: UserProfile[]): CrewMember[] {
  return users.map((u) => {
    const detail = ROSTER_ROLE_DETAIL[u.role] ?? DEFAULT_ROSTER_DETAIL;
    return {
      id: u.username,
      name: u.displayName,
      role: u.role,
      badge: detail.badge,
      color: detail.color,
      bio: detail.bio,
      agent: detail.agent,
    };
  });
}

/**
 * Resolve a crew accent colour name to its CSS variable. Mirrors
 * `ac()` in `_legacy/ws4-modals.js` lines 21-24 — note that legacy
 * also maps `cyan → sky`, and `red → melon` (red is desaturated to
 * avoid an alarming colour for a person card).
 */
export function crewAccentVar(color: CrewAccentColor | undefined): string {
  const map: Record<CrewAccentColor, string> = {
    blue: "true-blue",
    cyan: "sky",
    green: "green",
    orange: "gold",
    melon: "melon",
    red: "melon",
  };
  return `var(--accent-${map[color ?? "blue"]})`;
}

export function crewAccentRgb(color: CrewAccentColor | undefined): string {
  const map: Record<CrewAccentColor, string> = {
    blue: "true-blue",
    cyan: "sky",
    green: "green",
    orange: "gold",
    melon: "melon",
    red: "melon",
  };
  return `var(--accent-${map[color ?? "blue"]}-rgb)`;
}

const WS4_CREW: CrewMember[] = [
  {
    id: "ws4-lead",
    name: "Workshop Lead",
    badge: "LEAD",
    role: "Lead Facilitator",
    bio: "Owns the WS4 session arc — opening, activity flow, and outcome capture.",
    color: "blue",
    agent: {
      name: "XAES-OPS",
      type: "Session Conductor",
      description: "Keeps the agenda timed, captures decisions to the asset locker, and surfaces drift.",
      color: "blue",
    },
  },
  {
    id: "ws4-strategy",
    name: "Strategy Partner",
    badge: "STRATEGY",
    role: "Strategy",
    bio: "Holds the line on business outcomes and operating-model fit.",
    color: "melon",
    agent: {
      name: "XAES-PLAN",
      type: "Outcome Mapper",
      description: "Tracks how each decision maps to programme outcomes and flags drift early.",
      color: "melon",
    },
  },
  {
    id: "ws4-tech",
    name: "Tech Partner",
    badge: "TECH",
    role: "Technology",
    bio: "Translates agent capabilities and integration constraints for the room.",
    color: "green",
    agent: {
      name: "XAES-BUILD",
      type: "Capability Mapper",
      description: "Cross-references proposed automations against the current capability inventory.",
      color: "green",
    },
  },
  {
    id: "ws4-ops",
    name: "Ops Partner",
    badge: "OPS",
    role: "Operations",
    bio: "Anchors process realities, exception handling, and ground-truth workflows.",
    color: "orange",
    agent: {
      name: "XAES-FLOW",
      type: "Process Auditor",
      description: "Surfaces the exception paths and edge cases the room hasn't named yet.",
      color: "orange",
    },
  },
];

export const crewByWorkshop: Record<Workshop["id"], CrewMember[]> = {
  WS1: [],
  WS2: [],
  WS3: [],
  WS4: WS4_CREW,
  WS5: [],
  WS6: [],
  WS7: [],
  WS8: [],
};

/** Footer label shown below the crew grid in MeetCrewModal. Mirrors
 *  legacy `crew-footer-label` (`renderCrewModal` in
 *  `_legacy/ws4-modals.js`). */
export const crewFooterByWorkshop: Partial<Record<Workshop["id"], string>> = {
  WS4: "WS4 // HUMAN-AGENT WAYS OF WORKING",
};
